import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { formatDate } from '../utils/formatDate';
import {
  Card, CardActionArea, CardContent, Checkbox, Stack,
  Typography, Box, Button, IconButton, Divider
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PickingDetailPage = ({ warehouseId, uuid }) => {
  const { id } = useParams();  // URLパラメータから order_id を取得
  const [dataset, setData] = useState(null);  // 注文詳細を格納するステート
  //const [selectedItem, setSelectedItem] = useState(null);  // 選択されたアイテム
  const [error, setError] = useState(null);  // エラーメッセージ
  const [allChecked, setAllChecked] = useState(false);  // チェックボックスが全て選択されているかどうか
  const [starting, setStarting] = useState(false);
  const location = useLocation();
  const initialStarted = location.state?.started ?? false;
  const [started, setStarted] = useState(initialStarted);
  const [startError, setStartError] = useState(null);
  const navigate = useNavigate();

  // データをAPIから取得
  const fetchData = async () => {
    try {
      const response = await axiosInstance.post(`/order/${id}`, { warehouse_id: warehouseId, });  // 注文詳細をAPIから取得
      setData(response.data);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    fetchData();  // コンポーネントがマウントされた時にデータを取得
  }, [id]);

  // チェックボックスがすべて選択されているかを確認
  useEffect(() => {
    if (dataset && dataset.items) {
      const pickedItemsCount = dataset.items.filter(item => item.picking_done_flag).length;
      const allChecked = pickedItemsCount === dataset.items.length;
      setAllChecked(allChecked);  // すべてのチェックが選択されている場合に完了ボタンを有効にする
    }
  }, [dataset]);
  const handleStart = async () => {
    if (starting || started) return;

    try {
      setStarting(true);

      const res = await axiosInstance.post(`/picking/${id}/start`, {
        warehouse_id: warehouseId,
        device_ip: uuid
      });

      setStarted(true);

    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setStarted(true);
      }

      setStartError(err.response?.data?.message || '開始処理に失敗しました');
      console.error(err);

    } finally {
      setStarting(false);
    }
  };
  const handleCompleteClick = async () => {
    try {
      await axiosInstance.post(`/picking/${id}/complete`, { warehouse_id: warehouseId });
      navigate('/picking');
    } catch (err) {
      console.error(err);
    }
  };

  const handleItemClick = (item) => {
    if (!started) {
      setStartError('先にピッキング開始ボタンを押してください');
      return;
    }

    // setSelectedItem(item);

    navigate(`/picking/${id}/check/${item.order_item_id}`, {
      state: { orderItem: item, dataset },
    });
  };
  if (error) return <GlobalError errorMessage={
    error?.response?.data?.message || 'データの取得に失敗しました'
  } />;
  if (!dataset) return <div>データが見つかりません</div>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => navigate('/picking')}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography variant="h1" fontWeight="bold">No.{dataset.order_number}</Typography>
      </Stack >
      <Divider sx={{ my: 2 }} />
      <Stack spacing={0.5} sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">お客様名</Typography>
        <Typography variant="body1">{dataset.client_name} {dataset.branch_name}</Typography>
        <Typography variant="body1">{dataset.recipient_name}</Typography>
        <Typography variant="body2" color="text.secondary">納品場所</Typography>
        <Typography variant="body1">〒{dataset.postal_code} {dataset.state}{dataset.address_line}</Typography>
        <Typography variant="body2" color="text.secondary">配送希望日</Typography>
        <Typography variant="body1">{formatDate(dataset.delivery_date)}</Typography>
      </Stack>
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStart}
          disabled={starting || started}
          sx={{ minWidth: 280 }}
          size="large"
        >
          {started ? '開始済み' : starting ? '開始中...' : 'ピッキング開始'}
        </Button>
        {startError && (
          <Typography color="error" sx={{ mt: 1, fontSize: '0.9rem' }}>
            {startError}
          </Typography>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />
      {
        dataset.items && dataset.items.length > 0 ? (
          <Stack spacing={1}>
            {dataset.items.map((item) => (
              <Card key={item.order_item_id}>
                <CardActionArea onClick={() => handleItemClick(item)}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      component="img"
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url}`}
                      alt={item.product_name}
                      sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`;
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">{item.sku}</Typography>
                      <Typography variant="body1">{item.product_name}</Typography>
                      {item?.option_combination && Object.entries(item.option_combination).map(([type, value]) => (
                        <Typography variant="subtitle1"> {type} : {value}</Typography>
                      ))}
                      <Typography variant="body2" color="primary">数量 {item.quantity}個</Typography>
                    </Box>
                    <Box>
                      {item.picking_done_flag ? (
                        <Checkbox checked disabled />
                      ) : (
                        <ChevronRightIcon color="primary" />
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        ) : (
          <Typography>注文アイテムがありません</Typography>
        )
      }
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<TaskAltIcon />}
          onClick={handleCompleteClick}
          disabled={!allChecked}
          sx={{
            minWidth: 280,
          }}
          size='large'
        >
          完了
        </Button>
      </Box>
    </>
  );
};

export default PickingDetailPage;
